import {
  deviceType,
  findOrCreateDeviceId,
  getPageInstanceULID,
  pageIsDraft,
} from "@theconversation/shared-components/tcEventsCollector"
import { dispatchEvent } from "./dispatchEvent"
import { currentRegionFromMetaTag } from "@theconversation/shared-components/currentRegionFromMetaTag"
import { experimentRegistry } from "common/lib/abTesting/experimentRegistry"
import { UtmAttribution } from "@theconversation/shared-components/tcEventsCollector/tcEvent"

// This module implements a very simple event dispatch system.
// It will try to use a persistent deviceId for a given user's browser.
// The general schema of the events is common data at the top level, and event-specific
// data nested under a key named after the event.

// In the current iteration, article view and promo events are blocked if they are
// coming from a draft editor preview. Potentially a future version should instead
// set an attribute (like "location: draft") instead, but while this is an experiment
// it's better to keep things simple.

// In order to be able to mock this out in tests, we need to only fetch the value once methods are called.
// It's also better practice to fetch this on demand rather than at page load. So, this loads on demand
// and memoizes it.

export function trackPromoViewed(promoId: string, promoType: string, contentId = "") {
  if (pageIsDraft()) return

  const event = {
    eventType: "promoViewed",
    deviceId: findOrCreateDeviceId(),
    pageInstanceULID: getPageInstanceULID(),
    promoViewed: {
      contentId: contentId.toString(),
      promoId: promoId.toString(),
      promoType: promoType,
      deviceType: deviceType(),
    },
  }
  dispatchEvent(event)
}

export function trackPromoEngaged(promoId: string, promoType: string, contentId = "") {
  if (pageIsDraft()) return

  const event = {
    eventType: "promoEngaged",
    deviceId: findOrCreateDeviceId(),
    pageInstanceULID: getPageInstanceULID(),
    promoEngaged: {
      contentId: contentId.toString(),
      promoId: promoId.toString(),
      promoType: promoType,
      deviceType: deviceType(),
    },
  }
  dispatchEvent(event, true)
}

export function trackCallToActionViewed(callToActionId: string) {
  if (pageIsDraft()) return

  const event = {
    eventType: "callToActionViewed",
    deviceId: findOrCreateDeviceId(),
    pageInstanceULID: getPageInstanceULID(),
    callToActionViewed: {
      callToActionId: callToActionId,
      deviceType: deviceType(),
    },
  }
  dispatchEvent(event)
}

export function trackCallToActionEngaged(callToActionId: string) {
  if (pageIsDraft()) return

  const event = {
    eventType: "callToActionEngaged",
    deviceId: findOrCreateDeviceId(),
    pageInstanceULID: getPageInstanceULID(),
    callToActionEngaged: {
      callToActionId: callToActionId,
      deviceType: deviceType(),
    },
  }
  dispatchEvent(event, true)
}

export function trackNewsletterSubCardSelected({
  newsletterId,
  rank,
  userLoggedIn,
}: {
  newsletterId: string
  rank: number
  userLoggedIn: boolean
}) {
  const activeExperiment = experimentRegistry.getActiveExperiment()
  const event = {
    eventType: "newsletterSubCardSelected",
    activeEdition: currentRegionFromMetaTag(),
    userLoggedIn,
    deviceId: findOrCreateDeviceId(),
    pageInstanceULID: getPageInstanceULID(),
    ...(activeExperiment && {
      experiment: {
        experimentId: activeExperiment.experimentId,
        variantId: activeExperiment.variantId,
      },
    }),
    newsletterSubCardSelected: {
      newsletterId,
      rank,
      deviceType: deviceType(),
    },
  }
  dispatchEvent(event)
}

export function trackNewsletterSubCardDeselected({
  newsletterId,
  rank,
  userLoggedIn,
}: {
  newsletterId: string
  rank: number
  userLoggedIn: boolean
}) {
  const activeExperiment = experimentRegistry.getActiveExperiment()
  const event = {
    eventType: "newsletterSubCardDeselected",
    activeEdition: currentRegionFromMetaTag(),
    userLoggedIn,
    deviceId: findOrCreateDeviceId(),
    pageInstanceULID: getPageInstanceULID(),
    ...(activeExperiment && {
      experiment: {
        experimentId: activeExperiment.experimentId,
        variantId: activeExperiment.variantId,
      },
    }),
    newsletterSubCardDeselected: {
      newsletterId,
      rank,
      deviceType: deviceType(),
    },
  }
  dispatchEvent(event)
}

export function trackNewsletterSubscribed(
  newsletterId: string,
  userLoggedIn: boolean,
  attribution?: UtmAttribution,
) {
  if (pageIsDraft()) return

  const activeExperiment = experimentRegistry.getActiveExperiment()
  const event = {
    eventType: "newsletterSubscribed",
    activeEdition: currentRegionFromMetaTag(),
    userLoggedIn,
    deviceId: findOrCreateDeviceId(),
    pageInstanceULID: getPageInstanceULID(),
    ...(activeExperiment && {
      experiment: {
        experimentId: activeExperiment.experimentId,
        variantId: activeExperiment.variantId,
      },
    }),
    newsletterSubscribed: {
      newsletterId,
      deviceType: deviceType(),
      ...(attribution && { attribution }),
    },
  }
  dispatchEvent(event)
}

export function trackPageViewed({
  urlPath,
  userLoggedIn,
  category,
  attribution,
}: {
  urlPath: string
  userLoggedIn: boolean
  category: string
  attribution?: UtmAttribution
}) {
  const activeExperiment = experimentRegistry.getActiveExperiment()
  const event = {
    eventType: "pageViewed",
    deviceId: findOrCreateDeviceId(),
    pageInstanceULID: getPageInstanceULID(),
    userLoggedIn,
    activeEdition: currentRegionFromMetaTag(),
    ...(activeExperiment && {
      experiment: {
        experimentId: activeExperiment.experimentId,
        variantId: activeExperiment.variantId,
      },
    }),
    pageViewed: {
      deviceType: deviceType(),
      category,
      urlPath,
      ...(attribution && { attribution }),
    },
  }
  dispatchEvent(event)
}
